import * as React from "react";
import { Check, ChevronDown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import Header from "@/reusable_sections/Header";
import Footer from "@/reusable_sections/Footer";
const conditions = [{
  symptom: "Disc herniation with nerve compression",
  desc: "Severe back or leg pain, numbness, tingling or muscle weakness caused by a herniated disc pressing on a nerve root."
}, {
  symptom: "Spinal stenosis",
  desc: "Narrowing of the spinal canal causing persistent pain, weakness or difficulty walking."
}, {
  symptom: "Vertebral damage from osteoporosis or fracture",
  desc: "Vertebral compression fractures causing acute or chronic back pain and reduced spinal stability."
}, {
  symptom: "Failed conservative treatment",
  desc: "Ongoing symptoms that have not responded to injections, physiotherapy or medication over an appropriate period."
}, {
  symptom: "Worsening neurological symptoms",
  desc: "Progressive weakness, loss of sensation or deteriorating motor function requiring surgical assessment."
}];
const procedures = [{
  title: "Microdiscectomy",
  what: "A minimally invasive surgical procedure for patients whose disc herniation is compressing nerve roots or the spinal canal, causing severe pain, numbness, muscle weakness or loss of motor function that has not responded to conservative treatment.",
  how: "The surgeon removes the herniated portion of the disc that is pressing on the nerve. The procedure is performed through a minimal incision using advanced optical instruments and microscopes, allowing precise removal of only the damaged disc material. Recovery is faster and the risk of complications is lower than with traditional open surgery."
}, {
  title: "Nucleoplasty",
  what: "A minimally invasive spinal procedure aimed at reducing pressure within an intervertebral disc. It is typically indicated when disc herniation is causing nerve compression, pain or numbness, but the spinal structure is not yet severely damaged.",
  how: "Under local anaesthesia and X-ray guidance, a specialised needle is introduced into the centre of the disc. An electrode is passed through the needle to reduce disc pressure by removing excess disc material. This relieves pressure on the nerve structures and reduces pain. The procedure is most commonly performed in the lumbar or cervical spine."
}, {
  title: "Vertebroplasty",
  what: "A minimally invasive spinal procedure designed to stabilise vertebrae and reduce pain in patients whose vertebral bodies have been damaged by osteoporosis, trauma or oncological conditions.",
  how: "Medical cement is injected through a needle into the damaged vertebral body. The cement sets within minutes, stabilising the vertebra, reducing pain and restoring the load-bearing function of the spine. The procedure is performed under local or general anaesthesia with X-ray guidance. Pain reduction is typically felt within a few days."
}];
function SpineHero() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="spine-surgery-hero" style={{
    marginTop: 0
  }} className="relative w-full overflow-hidden bg-primary flex items-center justify-center min-h-[32vh] md:min-h-[50vh] py-10 md:py-16">
      <div className="absolute inset-0 pointer-events-none opacity-40 bg-[#264256] z-10" aria-hidden="true" />
      <div ref={ref} className={cn("relative z-20 container mx-auto px-6 flex flex-col items-center text-center max-w-5xl transition-all duration-1000 ease-out transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
        <span className="font-default text-[12px] text-accent-foreground uppercase tracking-widest mb-8 block font-semibold">
          EU Cross-Border Healthcare Directive
        </span>
        <h1 className="font-default font-bold text-[38px] md:text-[46px] leading-[1.1] text-white tracking-normal mb-8 max-w-4xl">
          Spine surgery.<br />Performed in Kaunas.
        </h1>
        <p className="font-default font-medium text-base md:text-lg text-white/90 leading-relaxed max-w-2xl mb-10">
          Minimally invasive procedures performed by experienced neurosurgeons in Lithuania. Costs reimbursable through the EU Cross-Border Directive for Irish public patients.
        </p>
        <div className="flex flex-col sm:flex-row gap-5 w-full sm:w-auto">
          <Button asChild className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-10 text-sm uppercase tracking-widest hover:tracking-[0.06em] transition-all duration-300 border border-ring shadow-md">
            <Link to="/#request-your-consultation">Book a Consultation</Link>
          </Button>
          <Button asChild className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-10 text-sm uppercase tracking-widest hover:tracking-[0.06em] transition-all duration-300 border border-ring shadow-md">
            <Link to="#our-procedures">See our procedures</Link>
          </Button>
        </div>
      </div>
    </section>;
}
export default function SpineSurgeryCopy() {
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);
  return <>
      <Header />
      <SpineHero />
      <section id="spine-surgery-overview" className="bg-background text-foreground overflow-hidden ![padding:32px_0px_32px_0px]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">

          <div className="mb-12 md:mb-24">
            <div className="text-center mb-12">
              <h2 className="font-default text-3xl md:text-4xl font-bold text-primary mb-3">Find out if we can help you</h2>
              <p className="text-base text-muted-foreground font-default max-w-2xl mx-auto">The procedure you need is decided by your diagnosis and imaging, so your final cost is individual to you. Each price below includes the surgery, your treatment and inpatient care, with no hidden extras. We confirm your exact, personal costs before you travel.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {conditions.map((item, i) => <div key={i} className="bg-white rounded-lg border border-[#f1f3f5] shadow-sm p-6 flex flex-col gap-3">
                  <h3 className="font-default font-bold text-primary text-lg leading-snug">{item.symptom}</h3>
                  <p className="font-default text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </div>)}
            </div>
            <div className="mt-8 p-6 bg-[#eefaf4] rounded-lg border border-[#a9e5c9]">
              <p className="font-default text-[#264256] text-sm text-center">
                If you are unsure whether surgery is the right option, an online consultation with one of our neurosurgeons will give you a clear answer before you commit to anything.
              </p>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row items-stretch relative mb-12 md:mb-24 gap-6 md:gap-12">
            <div className="w-full lg:w-[55%] relative z-0">
              <div className="relative w-full h-full min-h-[220px] md:min-h-[400px]">
                <img src="https://charming-joey.10web.cloud/wp-content/uploads/2026/06/53164958_303370817026157_4202337856586252288_n-1-scaled.jpg" alt="Spine surgery at Clinic Agatas" className="w-full h-full object-cover rounded-lg shadow-xl" />
                <div className="absolute inset-0 bg-primary/5 mix-blend-overlay rounded-lg pointer-events-none"></div>
              </div>
            </div>
            <div className="w-full lg:w-[45%] relative z-10">
              <Card className="bg-white border-[#f1f3f5] shadow-2xl p-8 md:p-10 rounded-lg h-full">
                <CardContent className="p-0 flex flex-col gap-6 h-full">
                  <div className="space-y-3">
                    <h2 className="font-default text-3xl md:text-4xl font-bold text-primary leading-tight">Spine Surgery in Kaunas</h2>
                    <p className="text-base text-muted-foreground font-default leading-relaxed">
                      Three neurosurgeons with combined experience across hundreds of spinal procedures. Each case is reviewed individually, with a surgical plan built on your scans, clinical history and specific diagnosis.
                    </p>
                  </div>
                  <ul className="space-y-3">
                    {["Microdiscectomy", "Nucleoplasty", "Vertebroplasty"].map((item, i) => <li key={i} className="flex items-center gap-3 p-3 border border-[#f1f3f5] rounded-lg bg-white">
                        <div className="bg-accent/50 rounded-full p-1 shrink-0">
                          <Check className="w-4 h-4 text-chart-2" strokeWidth={3} />
                        </div>
                        <span className="text-primary font-default text-base leading-snug">{item}</span>
                      </li>)}
                  </ul>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-chart-2 shrink-0" strokeWidth={3} />
                      <span className="text-muted-foreground font-default text-sm">Every procedure performed under X-ray guidance</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-chart-2 shrink-0" strokeWidth={3} />
                      <span className="text-muted-foreground font-default text-sm">Post-operative follow-up included at no extra cost</span>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground font-default">Online consultation available before you travel.</p>
                  <div className="pt-6 border-t border-border mt-auto">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <div className="text-primary text-4xl font-default font-bold tracking-tight">€3,960</div>
                        <div className="text-sm text-muted-foreground mt-1 font-default">Starting price for surgery</div>
                      </div>
                      <Button size="lg" asChild className="w-full sm:w-auto text-base h-12 px-6 rounded-md shadow-lg bg-chart-2 text-primary hover:bg-chart-2/90 transition-all font-default font-bold">
                        <Link to="/#request-your-consultation">Book a consultation</Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div id="our-procedures" className="mb-12 md:mb-24">
            <div className="text-center mb-12">
              <h2 className="font-default text-3xl md:text-4xl font-bold text-primary mb-3">Our Surgical Procedures</h2>
              <p className="text-base text-muted-foreground font-default max-w-2xl mx-auto">
                Each procedure is selected based on your diagnosis and imaging. Select a procedure below to learn more.
              </p>
            </div>
            <div className="flex flex-col gap-3 max-w-3xl mx-auto">
              {procedures.map((proc, i) => <div key={i} className="bg-white rounded-lg border border-[#f1f3f5] shadow-sm overflow-hidden">
                  <button onClick={() => setOpenIndex(openIndex === i ? null : i)} className="w-full flex items-center justify-between px-6 py-5 text-left">
                    <span className="font-default font-bold text-primary text-base">{proc.title}</span>
                    <ChevronDown className={`w-5 h-5 text-[#65d19d] shrink-0 transition-transform duration-200 ${openIndex === i ? "rotate-180" : ""}`} />
                  </button>
                  {openIndex === i && <div className="px-6 pb-6 flex flex-col gap-4 border-t border-[#f1f3f5]">
                      <div className="pt-4">
                        <p className="font-default font-bold text-[#264256] text-sm mb-1">What is it?</p>
                        <p className="font-default text-muted-foreground text-sm leading-relaxed">{proc.what}</p>
                      </div>
                      <div>
                        <p className="font-default font-bold text-[#264256] text-sm mb-1">How is it performed?</p>
                        <p className="font-default text-muted-foreground text-sm leading-relaxed">{proc.how}</p>
                      </div>
                    </div>}
                </div>)}
            </div>
          </div>

          <div className="flex flex-col lg:flex-row-reverse items-stretch gap-6 md:gap-12 mb-12 md:mb-24">
            <div className="w-full lg:w-[45%] relative">
              <div className="relative w-full h-full min-h-[220px] md:min-h-[400px]">
                <img src="https://charming-joey.10web.cloud/wp-content/uploads/2026/06/DSC_4562-1-scaled.jpg" alt="Your journey to recovery" className="w-full h-full object-cover rounded-lg shadow-xl" src="https://charming-joey.10web.cloud/wp-content/uploads/2026/07/Konsultacinis-OK.jpg" />
              </div>
            </div>
            <div className="w-full lg:w-[55%] flex flex-col gap-6 justify-center">
              <div>
                <h2 className="font-default text-3xl md:text-4xl font-bold text-primary leading-tight mb-3">Your journey to recovery</h2>
                <p className="text-base text-muted-foreground font-default mb-6">
                  Spinal surgery requires careful preparation and a structured recovery plan. From your first online consultation to post-operative rehabilitation at our partner sanatorium in Birstonas, every step is coordinated by our team.
                </p>
                <p className="text-base text-muted-foreground font-default">We guide you through pre-operative blood tests, an in-person consultation the day before surgery, the procedure itself, and physiotherapy-supported recovery at Egle Rehabilitation Centre in Birstonas before your flight home.</p>
              </div>
              <div>
                <Button size="lg" asChild className="text-base h-12 px-8 rounded-md shadow-lg bg-[#264256] text-white hover:bg-[#264256]/90 transition-all font-default font-bold">
                  <Link to="/your-journey">See your full journey</Link>
                </Button>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <div className="text-center mb-12">
              <h2 className="font-default text-3xl md:text-4xl font-bold text-primary mb-3">Procedure Costs</h2>
              <p className="text-base text-muted-foreground font-default max-w-2xl mx-auto">The procedure you need is decided by your diagnosis and imaging, so your final cost is individual to you. Each price below includes the surgery, your treatment and inpatient care, with no hidden extras. We confirm your exact, personal costs before you travel.</p>
            </div>
            <div className="bg-white rounded-lg border border-[#f1f3f5] shadow-sm overflow-hidden mb-8">
              <div className="bg-[#264256] px-6 py-4">
                <h3 className="font-default font-bold text-white text-lg">Getting Started</h3>
              </div>
              <div className="grid grid-cols-3 divide-x divide-[#f1f3f5]">
                {[{
                name: "In-person / Online consultation",
                price: "€180"
              }, {
                name: "Repeat consultation",
                price: "€90"
              }, {
                name: "Follow-up after surgery",
                price: "€0"
              }].map((proc, i) => <div key={i} className="flex flex-col items-center justify-center px-6 py-6 text-center gap-2">
                    <span className="font-default text-[#264256] text-sm">{proc.name}</span>
                    <span className="font-default font-bold text-[#264256] text-2xl">{proc.price}</span>
                  </div>)}
              </div>
            </div>
            <div className="bg-white rounded-lg border border-[#f1f3f5] shadow-sm overflow-hidden">
              <div className="bg-[#264256] px-6 py-4">
                <h3 className="font-default font-bold text-white text-lg">Surgical Procedures</h3>
              </div>
              <div className="grid grid-cols-3 divide-x divide-[#f1f3f5]">
                {[{
                name: "Microdiscectomy",
                price: "from €6,000"
              }, {
                name: "Nucleoplasty",
                price: "from €4,320"
              }, {
                name: "Vertebroplasty",
                price: "from €3,960"
              }].map((proc, i) => <div key={i} className="flex flex-col items-center justify-center px-6 py-6 text-center gap-2">
                    <span className="font-default text-[#264256] text-sm">{proc.name}</span>
                    <span className="font-default font-bold text-[#264256] text-2xl">{proc.price}</span>
                  </div>)}
              </div>
            </div>
            <div className="mt-8 p-6 bg-[#eefaf4] rounded-lg border border-[#a9e5c9]">
              <p className="font-default text-[#264256] text-sm text-center">
                Reimbursement under the EU Cross-Border Directive is subject to individual HSE assessment. We provide all required documentation to support your claim.
              </p>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild className="text-base h-14 px-10 rounded-md shadow-lg bg-[#264256] text-white hover:bg-[#264256]/90 transition-all font-default font-bold">
                <Link to="/#request-your-consultation">Book a consultation</Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="text-base h-14 px-10 rounded-md border-2 border-[#264256] text-[#264256] hover:bg-[#264256] hover:text-white transition-all font-default font-bold">
                <Link to="/our-specialists#neurosurgeons">Our specialists</Link>
              </Button>
            </div>
          </div>

        </div>
      </section>
      <Footer />
    </>;
}